
import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAppDataContext } from '@/contexts/AppDataContext';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { User } from '@/types';
import { getUserInitial } from '@/lib/utils';
import { apiService } from '@/services/api';
import {
  normalizeUserRole,
  getRoleLabel,
  ROLE_SELECT_OPTIONS,
} from '@/lib/roles';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';

const Users = () => {
  const { data, loading, refresh } = useAppDataContext();
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Check if current user is admin
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';
  
  // Ensure users is always an array to prevent errors
  const usersList = Array.isArray(data.users) ? data.users : [];
  
  // Get unique user roles for filtering
  const roles = [...new Set(usersList.map(user => user.role))];

  // This would normally come from users table, but we'll derive from employees for this demo
  const users: User[] = usersList.map(user => ({
    id: Number(user.id || user.id),
    username: `${user.username}`.trim(),
    email: user.email,
    role: normalizeUserRole(user.role),
    avatar: undefined
  }));

  // Filter users based on active tab
  const filteredUsers = activeTab === 'all' 
    ? users 
    : users.filter(user => user.role === activeTab);

  // Open modal for editing user role
  const handleEditClick = (user: User) => {
    if (!isAdmin) {
      toast.error('Only administrators can update user roles');
      return;
    }
    setSelectedUser(user);
    setSelectedRole(user.role);
    setIsModalOpen(true);
  };

  // Handle role update from modal
  const handleRoleUpdate = async () => {
    if (!selectedUser || !isAdmin) {
      toast.error('Only administrators can update user roles');
      return;
    }

    if (selectedRole === selectedUser.role) {
      toast.info('No changes to save');
      setIsModalOpen(false);
      return;
    }

    setIsUpdating(true);
    
    try {
      const response = await apiService.updateUser({
        id: selectedUser.id,
        role: selectedRole,
        requester_role: currentUser?.role || 'admin'
      } as any);
      
      // Backend returns { message, status, data }
      // handleResponse returns response.data which is the backend response object
      const message = response?.message || 'User role updated successfully';
      toast.success(message);
      setIsModalOpen(false);
      setSelectedUser(null);
      setSelectedRole('');
      // Refresh users list
      await refresh.users();
    } catch (error: any) {
      console.error('Error updating user role:', error);
      // Backend error response structure: error.response.data = { message, status, data }
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update user role';
      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    if (!isUpdating) {
      setIsModalOpen(false);
      setSelectedUser(null);
      setSelectedRole('');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold tracking-tight">Users Management</h1>
        </div>
        
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-payroll-primary"></div>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>System Users</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="all" onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="all">All Users</TabsTrigger>
                  <TabsTrigger value="admin">Administrators</TabsTrigger>
                  <TabsTrigger value="hr">HR Staff</TabsTrigger>
                  <TabsTrigger value="finance">Finance Staff</TabsTrigger>
                  <TabsTrigger value="audit">Internal Auditors</TabsTrigger>
                  <TabsTrigger value="secretary">Council Secretaries</TabsTrigger>
                  <TabsTrigger value="employee">Employees</TabsTrigger>
                </TabsList>
                
                <TabsContent value={activeTab}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        {isAdmin && <TableHead className="w-[100px]">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map(user => (
                        <TableRow key={user.id}>
                          <TableCell className="flex items-center space-x-3">
                            <Avatar>
                              <AvatarFallback>{user.username.charAt(0)}</AvatarFallback>
                              {user.avatar && <AvatarImage src={user.avatar} />}
                            </Avatar>
                            <span>{user.username}</span>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Badge variant={
                              user.role === 'admin' ? 'default' :
                              user.role === 'hr' ? 'secondary' :
                              user.role === 'finance' ? 'outline' :
                              user.role === 'audit' ? 'secondary' :
                              user.role === 'secretary' ? 'secondary' : 'secondary'
                            }>
                              {getRoleLabel(user.role)}
                            </Badge>
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditClick(user)}
                                className="gap-2"
                              >
                                <Pencil className="h-4 w-4" />
                                Edit
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Edit User Role Modal */}
        <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Edit User Role</DialogTitle>
              <DialogDescription>
                Update the role for {selectedUser?.username}. Only administrators can perform this action.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">User Information</label>
                <div className="flex items-center gap-3 p-3 border rounded-md">
                  <Avatar>
                    <AvatarFallback>{selectedUser?.username.charAt(0)}</AvatarFallback>
                    {selectedUser?.avatar && <AvatarImage src={selectedUser.avatar} />}
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedUser?.username}</p>
                    <p className="text-sm text-muted-foreground">{selectedUser?.email}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="role" className="text-sm font-medium">
                  Role
                </label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_SELECT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseModal}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRoleUpdate}
                disabled={isUpdating || selectedRole === selectedUser?.role}
              >
                {isUpdating ? 'Updating...' : 'Update Role'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Users;
